import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Edit, Factory, Loader2, Mail, MapPin, Package, Phone, User } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi, type BinderyVendorDetail } from "@/lib/api/bindery";
import { BinderyRequestStatusBadge } from "@/components/bindery/BinderyStatusBadge";
import { formatDate, formatMoney } from "./binderyFormatters";

const PAGE_STATES = { LOADING: "loading", READY: "ready", ERROR: "error" } as const;
type PageState = (typeof PAGE_STATES)[keyof typeof PAGE_STATES];

type VendorRequest = BinderyVendorDetail["requests"][number];

export default function BinderyVendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [state, setState] = useState<PageState>(PAGE_STATES.LOADING);
  const [vendor, setVendor] = useState<BinderyVendorDetail | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setState(PAGE_STATES.LOADING);
    try {
      const v = await binderyApi.vendors.getById(id);
      setVendor(v);
      setState(PAGE_STATES.READY);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to load vendor"));
      setState(PAGE_STATES.ERROR);
    }
  }, [id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (state === PAGE_STATES.LOADING) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (state === PAGE_STATES.ERROR || !vendor) {
    return (
      <EmptyState
        icon={<Factory className="w-6 h-6 text-slate-400" />}
        title="Vendor not found"
        description="This vendor may have been removed or is unavailable."
        action={<Button onClick={() => navigate("/bindery/vendors")}>Bindery vendors</Button>}
      />
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={vendor.name}
        description={vendor.contactPerson ?? "Bindery / repair vendor"}
        showSearch={false}
        breadcrumbItems={[
          { label: "Bindery vendors", href: "/bindery/vendors" },
          { label: vendor.name },
        ]}
        titleSize="md"
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge variant={vendor.isActive ? "success" : "neutral"}>
              {vendor.isActive ? "Active" : "Inactive"}
            </StatusBadge>
            <Button
              variant="outline"
              onClick={() => navigate(`/bindery/vendors/${vendor.id}/edit`)}
            >
              <Edit className="w-4 h-4 mr-2" /> Edit
            </Button>
          </div>
        }
      />

      <VendorProfileCard vendor={vendor} />
      <VendorRequestsCard
        requests={vendor.requests}
        onOpen={(rid) => navigate(`/bindery/requests/${rid}`)}
      />
    </PageContainer>
  );
}

function VendorProfileCard({ vendor }: { vendor: BinderyVendorDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Vendor profile</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-3">
        <InfoRow
          icon={<User className="w-4 h-4" />}
          label="Contact person"
          value={vendor.contactPerson ?? "—"}
        />
        <InfoRow
          icon={<Mail className="w-4 h-4" />}
          label="Email"
          value={
            vendor.email ? (
              <a href={`mailto:${vendor.email}`} className="text-primary hover:underline">
                {vendor.email}
              </a>
            ) : (
              "—"
            )
          }
        />
        <InfoRow
          icon={<Phone className="w-4 h-4" />}
          label="Phone"
          value={
            vendor.phone ? (
              <a href={`tel:${vendor.phone}`} className="text-primary hover:underline">
                {vendor.phone}
              </a>
            ) : (
              "—"
            )
          }
        />
        <InfoRow
          icon={<MapPin className="w-4 h-4" />}
          label="Address"
          value={vendor.address ?? "—"}
          full
        />
        <InfoRow
          label="Default turnaround"
          value={`${vendor.defaultTurnaroundDays} day${vendor.defaultTurnaroundDays === 1 ? "" : "s"}`}
        />
        <InfoRow
          label="Total batches"
          value={String(vendor._count?.requests ?? vendor.requests.length)}
        />
        {vendor.notes && <InfoRow label="Notes" value={vendor.notes} full />}
      </CardContent>
    </Card>
  );
}

function VendorRequestsCard({
  requests,
  onOpen,
}: {
  requests: VendorRequest[];
  onOpen: (id: string) => void;
}) {
  const columns: DataTableColumn<VendorRequest>[] = [
    {
      id: "requestNumber",
      header: "Request #",
      cell: (r) => (
        <button
          onClick={() => onOpen(r.id)}
          className="font-mono text-sm text-primary hover:underline"
        >
          {r.requestNumber}
        </button>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (r) => <BinderyRequestStatusBadge status={r.status} />,
      width: "w-36",
    },
    {
      id: "sent",
      header: "Sent",
      cell: (r) => <span className="text-sm">{formatDate(r.sentAt)}</span>,
      hideOnMobile: true,
    },
    {
      id: "expected",
      header: "Expected back",
      cell: (r) => <span className="text-sm">{formatDate(r.expectedReturnAt)}</span>,
      hideOnMobile: true,
    },
    {
      id: "cost",
      header: "Cost",
      cell: (r) => (
        <span className="text-sm tabular-nums font-medium">{formatMoney(r.costTotal)}</span>
      ),
      align: "right",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent batches</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={requests}
          rowKey={(r) => r.id}
          emptyState={
            <EmptyState
              icon={<Package className="h-8 w-8" />}
              title="No batches yet"
              description="Requests sent to this vendor will appear here."
            />
          }
        />
      </CardContent>
    </Card>
  );
}

function InfoRow({
  icon,
  label,
  value,
  full,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? "md:col-span-3" : undefined}>
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-sm text-slate-800">{value}</div>
    </div>
  );
}
